'use client';

import { useCallback, useMemo, useState } from 'react';
import logCustomRouteButtonClicked from '@alltrails/amplitude/events/logCustomRouteButtonClicked';
import FABButtonLocation from '@alltrails/amplitude/enums/FABButtonLocation';
import Button from '@alltrails/denali/components/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import IconButton from '@alltrails/denali/components/IconButton';
import DrawPath from '@alltrails/denali/icons/DrawPath';
import useUser from '@alltrails/context/hooks/useUser';
import dynamic from 'next/dynamic';
import { useExperiments } from '@alltrails/experiments';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type Props = {
  trailSlug: string;
  trailId: number;
  isMobileOverviewButton?: boolean;
  isDesktopCta?: boolean;
  analyticsLocation: FABButtonLocation;
};

export default function CustomizeRouteButton({ trailSlug, trailId, analyticsLocation, isMobileOverviewButton, isDesktopCta }: Props) {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const hasFreeBYOT = useExperiments()?.['web-discover-byot-free-rm']?.value === 'on';
  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const currentPath = window.location.pathname + window.location.search;
    return encodeURIComponent(currentPath);
  }, []);

  const onCustomizeRouteClick = useCallback(() => {
    logCustomRouteButtonClicked({ location: analyticsLocation, route_loaded: true, trail_id: trailId });
    if (user) {
      window.location.assign(wrapUrlSafe(`/explore/custom-routes/trail/${trailSlug}${returnTo ? `?returnTo=${returnTo}` : ''}`, languageRegionCode));
    } else {
      setIsSignUpModalOpen(true);
    }
  }, [languageRegionCode, trailSlug, trailId, analyticsLocation, user, returnTo]);

  return (
    <>
      {isMobileOverviewButton ? (
        <Button
          onClick={onCustomizeRouteClick}
          text={<FormattedMessage defaultMessage="Customize route" />}
          testId="customize-route-button"
          width="full"
        />
      ) : isDesktopCta ? (
        <IconButton
          testId="customize-route-cta"
          icon={{ Component: DrawPath }}
          onClick={onCustomizeRouteClick}
          title={intl.formatMessage({ defaultMessage: 'Customize route' })}
        />
      ) : null}
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            window.location.assign(wrapUrlSafe(`/explore/custom-routes/trail/${trailSlug}`, languageRegionCode));
          }}
          trigger="custom routes trail page cta tapped"
          upsellTrigger="custom routes trail page cta tapped"
          hideUpsell={hasFreeBYOT}
        />
      )}
    </>
  );
}
